import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { DwOrmDataService, DwOrmDataServiceToken } from '@devwareapps/devware-cap';
import { combineLatest, of, Subscription } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { AppMetaData, ChallengeTeamEntity, ChallengeTeamJoinRequestEntity, ChallengeTeamJoinRequestStatusAllItems } from '../../../../meta-data/app-meta-data.service';

@Component({ selector: 'app-captain-approvals', templateUrl: './captain-approvals.component.html',
    styles: [`:host { display: block; margin-top: 1.5rem; } section { padding: 1.25rem; border: 1px solid #e4e0d4; border-radius: .75rem; }
      .request { display: flex; gap: .75rem; justify-content: space-between; flex-wrap: wrap; padding: 1rem 0; border-top: 1px solid #e4e0d4; }
      .actions { display: flex; gap: .5rem; } input { max-width: 100%; } .request-heading {display:flex;align-items:center;justify-content:space-between;gap:1rem;flex-wrap:wrap;margin-top:1rem;} .request-heading h3 {margin:0;} .request-count {display:inline-grid;place-items:center;background:#be193b;color:white;border-radius:50%;min-width:28px;height:28px;font-size:14px;margin-left:.5rem;} .request {align-items:center;flex-wrap:nowrap;padding:1.25rem 0;} .request-body {flex:1;min-width:0;} time {color:#747b83;font-size:.85rem;margin-left:.5rem;} .request-note {background:#faf9f4;border:1px solid #e4e0d4;border-radius:12px;padding:.75rem;margin:.5rem 0 0;white-space:pre-wrap;overflow-wrap:anywhere;} .request-status {color:#747b83;font-size:.85rem;} .actions {flex:none;} @media(max-width:600px) {.request {flex-wrap:wrap;} .request-body {flex-basis:calc(100% - 60px);} .actions {margin-left:52px;} }`], })
export class CaptainApprovalsComponent implements OnChanges, OnDestroy {
    @Input() teamId: number;
    @Output() reviewed = new EventEmitter<void>();
    requests: ChallengeTeamJoinRequestEntity[] = [];
    loading = false;
    busy = false;
    error = '';
    message = '';
    code = '';
    codeError = '';
    requiresApproval = true;
    showCompleted = false;
    get pendingCount(): number { return this.requests.filter(request => request.ChallengeTeamJoinRequestStatusId === ChallengeTeamJoinRequestStatusAllItems.Pending).length; }
    get visibleRequests(): ChallengeTeamJoinRequestEntity[] {
        return this.requests.filter(request => this.showCompleted || request.ChallengeTeamJoinRequestStatusId === ChallengeTeamJoinRequestStatusAllItems.Pending);
    }
    requestName(request: ChallengeTeamJoinRequestEntity): string {
        const person = request.UserChallenge?.UserProfile?.DwUser;
        return [person?.FirstName, person?.LastName].filter(Boolean).join(' ') || 'Streaker';
    }
    pending(request: ChallengeTeamJoinRequestEntity): boolean { return request.ChallengeTeamJoinRequestStatusId === ChallengeTeamJoinRequestStatusAllItems.Pending; }
    private subscriptions = new Subscription();

    ngOnChanges(): void { this.subscriptions.unsubscribe(); this.subscriptions = new Subscription(); this.message = ''; this.busy = false; this.refresh(); }
    constructor(@Inject(DwOrmDataServiceToken) private orm: DwOrmDataService) {}
    get shareLink(): string {
        if (!this.code) { return ''; }
        const url = new URL('challenge-registration', document.baseURI);
        url.searchParams.set('team_code', this.code);
        return url.toString();
    }
    refresh(): void {
        if (this.busy || !this.teamId) { return; }
        this.loading = true; this.error = ''; this.requests = []; this.code = ''; this.codeError = '';
        const person = AppMetaData.DwUser.CreateQueryBuilder().addFields(a => [a.FirstName, a.LastName, a.UserPictureMediaId]);
        const profile = AppMetaData.UserProfile.CreateQueryBuilder().addPrefetch(r => r.DwUser, person.query);
        const enrollment = AppMetaData.UserChallenge.CreateQueryBuilder().addPrefetch(r => r.UserProfile, profile.query);
        const query = AppMetaData.ChallengeTeamJoinRequest.CreateQueryBuilder()
            .addFields(a => [a.ChallengeTeamJoinRequestId, a.UserChallengeId, a.CreatedDateTime, a.RequestNotes, a.ChallengeTeamJoinRequestStatusId])
            .addFilterAnd(f => f.Equal(a => a.ChallengeTeamId, this.teamId))
            .addPrefetch(r => r.UserChallenge, enrollment.query)
            .addOrderByDesc(a => a.CreatedDateTime)
            .addOrderBy(a => a.ChallengeTeamJoinRequestId);
        query.getFieldSettings().LoadAllLookupDisplayFields = true;
        const teamQuery = AppMetaData.ChallengeTeam.CreateQueryBuilder()
            .addFields(a => [a.RequestApprovalToJoin])
            .addFilterAnd(f => f.Equal(a => a.ChallengeTeamId, this.teamId));
        this.subscriptions.add(combineLatest([this.orm.executeQuery<ChallengeTeamJoinRequestEntity>(query).pipe(take(1)),
            this.orm.executeQuery<ChallengeTeamEntity>(teamQuery).pipe(take(1))]).subscribe(([requests, teams]) => {
            this.requests = requests || [];
            this.requiresApproval = teams.length !== 1 || teams[0].RequestApprovalToJoin === true;
            this.loading = false;
        }, () => { this.loading = false; this.error = 'Unable to load pending requests. Refresh to try again.'; }));
        const codeQuery = AppMetaData.ChallengeTeam.CreateQueryBuilder()
            .addFields(a => [a.TeamJoinCode])
            .addFilterAnd(f => f.Equal(a => a.ChallengeTeamId, this.teamId));
        this.subscriptions.add(this.orm.executeQuery<ChallengeTeamEntity>(codeQuery).pipe(take(1), catchError(() => of([]))).subscribe(teams => {
            this.code = teams.length === 1 ? teams[0].TeamJoinCode || '' : '';
            if (!this.code) { this.codeError = 'The team sharing code is not available yet.'; }
        }));
    }
    review(request: ChallengeTeamJoinRequestEntity, approved: boolean): void {
        if (this.busy || this.loading || this.error || !this.pending(request) || !this.requests.includes(request)) { return; }
        this.busy = true; this.message = '';
        const update = AppMetaData.ChallengeTeamJoinRequest.CreateEntity();
        update.ChallengeTeamJoinRequestId = request.ChallengeTeamJoinRequestId;
        update.ChallengeTeamJoinRequestStatusId = approved ? ChallengeTeamJoinRequestStatusAllItems.Approved : ChallengeTeamJoinRequestStatusAllItems.Rejected;
        this.subscriptions.add(this.orm.saveEntity(update).pipe(take(1)).subscribe(() => {
            this.busy = false; this.message = approved ? 'Request approved.' : 'Request declined.'; this.refresh(); this.reviewed.emit();
        }, () => {
            this.busy = false; this.error = 'Unable to confirm this decision. Refresh requests before trying again.';
        }));
    }
    ngOnDestroy(): void { this.subscriptions.unsubscribe(); }
}
